import { groq } from "next-sanity"

import { getAdmAssignedCustomers } from "@/graphql/schemas/shared/utils/getAdmAssignedCustomers"
import { groqQueryOptions } from "@/graphql/schemas/shared/utils/groqQueryOptions"
import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"
import { AppSettings } from "@/settings"

import { checkPermissions } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { agreedTourProjection, tourPLanningProjection } from "../sanity/tourPlanning.projection"

export const queryAdmWeekTours = {
  async admWeekTours(parent, args, context, info) {
    const { week, lang, cdn, limit, offset, sort } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.TOURS,
      operation: Operations.READ,
    })

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient

    client.config({ ...config, useCdn: cdn })

    const { draftsFilter } = groqQueryOptions({ includeDrafts: true })

    const tourSchedules = await client.fetch(
      groq`*[_type=="tourSchedule" && calendarWeek==$week ${draftsFilter}] {
        ${tourPLanningProjection()}
      }`,
      { week },
    )

    if (!tourSchedules || tourSchedules.length <= 0) throw new Error("Tour was not found")

    const admAssignedCustomers = await getAdmAssignedCustomers({
      admNumber: user.loggedInAdm.admNumber,
      tourSchedules,
      limit,
      offset,
      sort,
    })

    if (admAssignedCustomers.results && admAssignedCustomers.total <= 0) return { total: 0, items: [] }

    const agreedTours = await client.fetch(
      groq`*[_type=="agreedTour" && calendarWeek==$week ]{
            ${agreedTourProjection()}}`,
      { week },
    )

    const totalVisitReportsQuery = getTotalVisitReportsQuery(admAssignedCustomers, user.loggedInAdm.admNumber)

    const totalVisitReports = (await EinzaGraphiQLService.getData(totalVisitReportsQuery))?.data

    return {
      limit,
      offset,
      total: admAssignedCustomers.total,
      items: admAssignedCustomers.results.map(async (assignedCustomer) => {
        const tourNumber = assignedCustomer?.custom?.fields?.tourNumber
        const { defaultBillingAddressId, addresses } = assignedCustomer

        const isTourAgreed = agreedTours.some(
          (agreedTour) => agreedTour.customerNumber === assignedCustomer?.customerNumber,
        )
        return {
          admNumber: user.loggedInAdm.admNumber,
          tourNumber,
          customer: {
            ...assignedCustomer,
            customFields: {
              customerBranchId:
                assignedCustomer.custom?.fields?.deliveryBranch ?? assignedCustomer.custom?.fields?.customerBranch,
              customerClassification: assignedCustomer.custom?.fields?.customerClassification ?? null,
              tourNumber: assignedCustomer.custom?.fields?.tourNumber ?? null,
            },
            defaultBillingAddress: addresses?.find((address) => defaultBillingAddressId === address.id) ?? null,
          },
          interval: String(tourNumber),
          isTourAgreed: isTourAgreed,
          total:
            (totalVisitReports && totalVisitReports[`reports_${assignedCustomer.customerNumber}`]?.totalElements) || 0,
          latest:
            (totalVisitReports &&
              totalVisitReports[`reports_${assignedCustomer.customerNumber}`]?.content[0]?.attendanceAt) ||
            null,
        }
      }),
    }
  },
}
function getTotalVisitReportsQuery(items, admNumber) {
  const graphQlQueryObject = []
  const filterValues = (admNumber) => {
    return [
      { field: "year", operator: "eq", value: new Date().getFullYear().toString() },
      { field: "creationBy", operator: "eq", value: admNumber },
    ]
  }

  items.results.forEach((customer) => {
    const query = {
      method: `reports_${customer?.customerNumber} : findVisitReportsByCustomerAndFilter`,
      variables: {
        customerNumber: customer?.customerNumber,
        filters: filterValues(admNumber),
        page: 0,
        size: 10,
        sort: "attendanceAt.desc",
      },
      fields: ["numberOfElements", "totalElements"],
      expandedFields: {
        content: ["attendanceAt"],
      },
    }
    graphQlQueryObject.push(query)
  })

  return EinzaGraphiQLService.formQuery(graphQlQueryObject)
}
